import { observer } from 'mobx-react-lite';
import { View, ViewStyle } from 'react-native';
import { useStore } from 'src/contexts/StoreContext';
import TextLato from '../text/text-lato/TextLato';
import { brand } from 'src/theme/colors';
import { Icon } from '../icon/Icon';
import { ICON_NAMES } from '../icon/icon.helper';
import { SliderAdjustments } from '../tools/slider-adjustments/SliderAdjustments';
import { useState } from 'react';
import { styles } from './BottomSheetAdjustments.style';
import { useAdjustmentsLogic } from '../tools/adjustments/Adjustments.logic';
import { ACTIVE_TOOLS } from 'src/store/page-project/pageProject.constants';
import { TestIDs } from 'src/constants/testIDs.constants';
import { adjustmentsTestID } from 'src/helpers/testIDs.helper';
import { FocusGroups } from '../focus-provider/FocusProvider.constants';
import { ADJUSTMENTS } from '../tools/adjustments/Adjustments.types';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { useRoute } from '@react-navigation/native';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { parseAnalyticsParamsByUserImageArea } from 'src/_shared/analytics.utils';
import { BottomSheetScrollView } from '@gorhom/bottom-sheet';
import { PressableHovered } from 'src/editor-v3/components/pressable-hovered/PressableHovered';
import { useFocusableGroups } from 'src/hooks/useFocusableGroup';

const ADJUSTMENT_TAB_ORDER = {
  [ADJUSTMENTS.BRIGHTNESS]: 0,
  [ADJUSTMENTS.CONTRAST]: 1,
  [ADJUSTMENTS.SATURATION]: 2,
};
const SLIDER_FOCUS_ORDER_OFFSET = 3;
const RESTORE_FOCUS_ORDER = 6;
const CLOSE_FOCUS_ORDER = 7;

export const BottomSheetAdjustments = observer(
  ({ bsStyle, justContent = false }: { bsStyle: ViewStyle; justContent?: boolean }) => {
    const {
      i18n,
      project: { activeUserImageArea, setActiveTool },
      bottomSheet: { closeBottomSheet },
    } = useStore();

    const route = useRoute();
    const newRoute = [ROUTE_NAMES.EditorNew].includes(route?.name as ROUTE_NAMES);

    useFocusableGroups([FocusGroups.BOTTOM_SHEET]);

    const {
      brightness,
      saturation,
      contrast,
      setBrightness,
      setContrast,
      setSaturation,
      setAdjustments,
      onPressRestore,
    } = useAdjustmentsLogic(activeUserImageArea?.userImage);
    const [activeAdjustment, setActiveAdjustment] = useState<ADJUSTMENTS>(ADJUSTMENTS.BRIGHTNESS);
    const [sliderWidth, setSliderWidth] = useState<number>(0);

    const isActive = (adjustment: ADJUSTMENTS) => {
      return activeAdjustment === adjustment;
    };

    const Component = (
      <View style={[styles.container, newRoute && { paddingBottom: 0, paddingHorizontal: 20, paddingTop: 8 }]}>
        <View
          style={styles.adjustmentsWrapper}
          onLayout={e => {
            setSliderWidth(e.nativeEvent.layout.width);
          }}
        >
          {isActive(ADJUSTMENTS.BRIGHTNESS) && (
            <>
              <View style={styles.adjustmentContainer}>
                <TextLato style={styles.adjustmentText}>{i18n.t('adjustments:brightness')}</TextLato>
                <TextLato style={styles.valueText}>{brightness.toString()}</TextLato>
              </View>
              <View>
                {sliderWidth && (
                  <SliderAdjustments
                    focusGroup={FocusGroups.BOTTOM_SHEET}
                    focusOrder={SLIDER_FOCUS_ORDER_OFFSET + 0}
                    focusId={adjustmentsTestID(TestIDs.BRIGHTNESS_SLIDER)}
                    testID={adjustmentsTestID(TestIDs.BRIGHTNESS_SLIDER)}
                    defaultValue={brightness}
                    sliderWidth={sliderWidth}
                    onChange={(value: number) => {
                      setBrightness(value);
                    }}
                    onChangeEnd={(value: number) => {
                      setAdjustments({ brightness: value, contrast, saturation });
                      amplitude.trackEvent(AnalyticsEvents.modifyPhoto.adjustClick, {
                        ...parseAnalyticsParamsByUserImageArea(activeUserImageArea),
                        adjust_choice: ADJUSTMENTS.BRIGHTNESS,
                      });
                    }}
                  />
                )}
              </View>
            </>
          )}
          {isActive(ADJUSTMENTS.CONTRAST) && (
            <>
              <View style={styles.adjustmentContainer}>
                <TextLato style={styles.adjustmentText}>{i18n.t('adjustments:contrast')}</TextLato>
                <TextLato style={styles.valueText}>{contrast.toString()}</TextLato>
              </View>
              <View>
                {sliderWidth && (
                  <SliderAdjustments
                    focusGroup={FocusGroups.BOTTOM_SHEET}
                    focusOrder={SLIDER_FOCUS_ORDER_OFFSET + 1}
                    focusId={adjustmentsTestID(TestIDs.CONTRAST_SLIDER)}
                    testID={adjustmentsTestID(TestIDs.CONTRAST_SLIDER)}
                    defaultValue={contrast}
                    sliderWidth={sliderWidth}
                    onChange={(value: number) => {
                      setContrast(value);
                    }}
                    onChangeEnd={(value: number) => {
                      setAdjustments({ brightness, contrast: value, saturation });
                      amplitude.trackEvent(AnalyticsEvents.modifyPhoto.adjustClick, {
                        ...parseAnalyticsParamsByUserImageArea(activeUserImageArea),
                        adjust_choice: ADJUSTMENTS.CONTRAST,
                      });
                    }}
                  />
                )}
              </View>
            </>
          )}
          {isActive(ADJUSTMENTS.SATURATION) && (
            <>
              <View style={styles.adjustmentContainer}>
                <TextLato style={styles.adjustmentText}>{i18n.t('adjustments:saturation')}</TextLato>
                <TextLato style={styles.valueText}>{saturation.toString()}</TextLato>
              </View>
              <View>
                {sliderWidth && (
                  <SliderAdjustments
                    focusGroup={FocusGroups.BOTTOM_SHEET}
                    focusOrder={SLIDER_FOCUS_ORDER_OFFSET + 2}
                    focusId={adjustmentsTestID(TestIDs.SATURATION_SLIDER)}
                    testID={adjustmentsTestID(TestIDs.SATURATION_SLIDER)}
                    defaultValue={saturation}
                    sliderWidth={sliderWidth}
                    onChange={(value: number) => {
                      setSaturation(value);
                    }}
                    onChangeEnd={(value: number) => {
                      setAdjustments({ brightness, contrast, saturation: value });
                      amplitude.trackEvent(AnalyticsEvents.modifyPhoto.adjustClick, {
                        ...parseAnalyticsParamsByUserImageArea(activeUserImageArea),
                        adjust_choice: ADJUSTMENTS.SATURATION,
                      });
                    }}
                  />
                )}
              </View>
            </>
          )}
        </View>
        <View>
          <View style={[styles.adjustmentsButtonsContainer, , { paddingBottom: 0 }]}>
            <PressableHovered
              style={[
                styles.adjustmentButton,
                isActive(ADJUSTMENTS.BRIGHTNESS) && { backgroundColor: brand.main.variations['lighterWater-3'] },
              ]}
              onPress={() => {
                setActiveAdjustment(ADJUSTMENTS.BRIGHTNESS);
              }}
              testID={adjustmentsTestID(`${ADJUSTMENTS.BRIGHTNESS}-tab`)}
              focusId={adjustmentsTestID(`${ADJUSTMENTS.BRIGHTNESS}-tab`)}
              focusGroup={FocusGroups.BOTTOM_SHEET}
              focusOrder={ADJUSTMENT_TAB_ORDER[ADJUSTMENTS.BRIGHTNESS]}
              accessibilityLabel={i18n.t('actions:edit_x', { x: i18n.t('adjustments:brightness') })}
              accessibilityRole="button"
            >
              <Icon name={ICON_NAMES.Sun} />
            </PressableHovered>
            <PressableHovered
              style={[
                styles.adjustmentButton,
                isActive(ADJUSTMENTS.CONTRAST) && { backgroundColor: brand.main.variations['lighterWater-3'] },
              ]}
              onPress={() => {
                setActiveAdjustment(ADJUSTMENTS.CONTRAST);
              }}
              testID={adjustmentsTestID(`${ADJUSTMENTS.CONTRAST}-tab`)}
              focusId={adjustmentsTestID(`${ADJUSTMENTS.CONTRAST}-tab`)}
              focusGroup={FocusGroups.BOTTOM_SHEET}
              focusOrder={ADJUSTMENT_TAB_ORDER[ADJUSTMENTS.CONTRAST]}
              accessibilityLabel={i18n.t('actions:edit_x', { x: i18n.t('adjustments:contrast') })}
              accessibilityRole="button"
            >
              <Icon name={ICON_NAMES.Contrast} />
            </PressableHovered>
            <PressableHovered
              style={[
                styles.adjustmentButton,
                isActive(ADJUSTMENTS.SATURATION) && { backgroundColor: brand.main.variations['lighterWater-3'] },
              ]}
              onPress={() => {
                setActiveAdjustment(ADJUSTMENTS.SATURATION);
              }}
              testID={adjustmentsTestID(`${ADJUSTMENTS.SATURATION}-tab`)}
              focusId={adjustmentsTestID(`${ADJUSTMENTS.SATURATION}-tab`)}
              focusGroup={FocusGroups.BOTTOM_SHEET}
              focusOrder={ADJUSTMENT_TAB_ORDER[ADJUSTMENTS.SATURATION]}
              accessibilityLabel={i18n.t('actions:edit_x', { x: i18n.t('adjustments:saturation') })}
              accessibilityRole="button"
            >
              <Icon name={ICON_NAMES.Drop} />
            </PressableHovered>
          </View>
          <View style={[styles.ctasContainer, newRoute && { paddingTop: 12 }]}>
            <PressableHovered
              style={styles.ctaButtonSecondary}
              onPress={onPressRestore}
              testID={adjustmentsTestID(TestIDs.RESTORE)}
              focusId={adjustmentsTestID(TestIDs.RESTORE)}
              focusGroup={FocusGroups.BOTTOM_SHEET}
              focusOrder={RESTORE_FOCUS_ORDER}
              accessibilityLabel={i18n.t('adjustments:restore')}
              accessibilityRole="button"
            >
              <TextLato style={{ ...styles.ctaText, color: brand.main.water }}>
                {i18n.t('adjustments:restore')}
              </TextLato>
            </PressableHovered>
            <PressableHovered
              style={styles.ctaButton}
              onPress={() => {
                closeBottomSheet();
                setActiveTool(ACTIVE_TOOLS.frame);
              }}
              testID={adjustmentsTestID(TestIDs.CLOSE)}
              focusId={adjustmentsTestID(TestIDs.CLOSE)}
              focusGroup={FocusGroups.BOTTOM_SHEET}
              focusOrder={CLOSE_FOCUS_ORDER}
              accessibilityLabel={i18n.t('adjustments:close')}
              accessibilityRole="button"
            >
              <TextLato style={styles.ctaText}>{i18n.t('adjustments:close')}</TextLato>
            </PressableHovered>
          </View>
        </View>
      </View>
    );

    return justContent ? (
      <View style={{ paddingBottom: 12, paddingHorizontal: 12 }}>{Component}</View>
    ) : (
      <BottomSheetScrollView contentContainerStyle={bsStyle}>{Component}</BottomSheetScrollView>
    );
  },
);
